import { useUpdateExistingSubscription } from './useUpdateExistingSubscription';

import { logger } from '@heidi/runtime/logger';

import { type Period } from '@heidi/commerce/contracts';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCallback, useRef, useState } from 'react';
import { getCommerceBillingUiPorts } from '../billing-ui-ports';

export type UseSubscriptionUpgradeOptions = {
  onCheckoutComplete?: () => void;
  /** Called when Stripe V2 `mutate` fails (in addition to global mutation error handling). */
  onCheckoutError?: () => void;
  /** When set, forwarded to Stripe V2 checkout as `free_trial_days`. */
  freeTrialDays?: number;
};

export function useSubscriptionUpgrade({
  onCheckoutComplete,
  onCheckoutError,
  freeTrialDays,
}: UseSubscriptionUpgradeOptions = {}) {
  const { mutate: createCheckout, isPending: isStripeCheckoutPending } =
    getCommerceBillingUiPorts().useMutateCheckoutV2();
  const {
    hasExistingStripePaidSubscription,
    isBillingInfoLoaded,
    redirectToBillingPortalUpdate,
  } = useUpdateExistingSubscription();
  // `useMutateCheckoutV2.isPending` only covers Stripe Checkout; the billing-portal
  // redirect is a separate async fetch + navigation, so track it here and OR into
  // `isPending` so CTAs disable and double-clicks cannot fire duplicate redirects.
  const [isBillingPortalPending, setIsBillingPortalPending] = useState(false);
  const isBillingPortalInFlightRef = useRef(false);
  const isCheckoutInFlightRef = useRef(false);
  const isCheckoutPending = isStripeCheckoutPending;
  const isPending = isCheckoutPending || isBillingPortalPending;

  const checkoutMutateOptions = useCallback(
    () => ({
      ...(onCheckoutComplete ? { onSuccess: onCheckoutComplete } : {}),
      ...(onCheckoutError ? { onError: onCheckoutError } : {}),
      onSettled: () => {
        isCheckoutInFlightRef.current = false;
      },
    }),
    [onCheckoutComplete, onCheckoutError]
  );

  const stripeCheckoutBase = useCallback(
    () => ({
      fe_endpoint: getCommerceBillingUiPorts().getStripeCallbackRedirect(false),
      fe_return_endpoint: getCommerceBillingUiPorts().getStripeReturnRedirect(),
      ...(!!freeTrialDays ? { free_trial_days: freeTrialDays } : {}),
    }),
    [freeTrialDays]
  );

  const performStripeCheckout = useCallback(
    (period: Period, tier: SubscriptionTier) => {
      createCheckout(
        {
          plan_period: period,
          tier,
          // not passing isInReverseTrial because the stripe success modal hasn't been updated to reflect new tiers
          ...stripeCheckoutBase(),
        },
        checkoutMutateOptions()
      );
    },
    [createCheckout, checkoutMutateOptions, stripeCheckoutBase]
  );

  const performStripeCheckoutByPriceId = useCallback(
    (period: Period, priceId: string) => {
      createCheckout(
        {
          price_id: priceId,
          plan_period: period,
          ...stripeCheckoutBase(),
        },
        checkoutMutateOptions()
      );
    },
    [createCheckout, checkoutMutateOptions, stripeCheckoutBase]
  );

  /** Clinician Max checkout — `price_id` on the wire; `period` is analytics-only (GRO-4369). */
  const handleUpgradeByPriceId = useCallback(
    async (period: Period, priceId: string) => {
      if (getCommerceBillingUiPorts().isAppleCompliantBuild()) {
        logger.warn('[compliance] Upgrade suppressed on Apple-compliant build');
        return;
      }

      if (
        isCheckoutPending ||
        isBillingPortalInFlightRef.current ||
        isCheckoutInFlightRef.current
      ) {
        return;
      }

      if (!isBillingInfoLoaded) {
        return;
      }

      // A trialling subscription has never been paid for, so it upgrades through
      // Checkout like any other unpaid user — only a paid sub swaps price in the portal.
      if (hasExistingStripePaidSubscription) {
        isBillingPortalInFlightRef.current = true;
        setIsBillingPortalPending(true);
        try {
          await redirectToBillingPortalUpdate(onCheckoutComplete, { price_id: priceId });
        } finally {
          isBillingPortalInFlightRef.current = false;
          setIsBillingPortalPending(false);
        }
        return;
      }
      isCheckoutInFlightRef.current = true;
      performStripeCheckoutByPriceId(period, priceId);
    },
    [
      isCheckoutPending,
      isBillingInfoLoaded,
      hasExistingStripePaidSubscription,
      redirectToBillingPortalUpdate,
      onCheckoutComplete,
      performStripeCheckoutByPriceId,
    ]
  );

  const handleUpgrade = useCallback(
    async (period: Period, tier: SubscriptionTier, priceId?: string | null) => {
      if (priceId === null) {
        logger.warn('[billing] Upgrade blocked because v3 price_id is unavailable');
        return;
      }
      if (priceId !== undefined) {
        await handleUpgradeByPriceId(period, priceId);
        return;
      }

      if (getCommerceBillingUiPorts().isAppleCompliantBuild()) {
        logger.warn('[compliance] Upgrade suppressed on Apple-compliant build');
        return;
      }

      if (
        isCheckoutPending ||
        isBillingPortalInFlightRef.current ||
        isCheckoutInFlightRef.current
      ) {
        return;
      }

      // Evidence Plus checkout retired (GRO-3609) — always Clinician.
      const effectiveTier =
        tier === SubscriptionTier.EVIDENCE ? SubscriptionTier.CLINICIAN_PRO : tier;

      if (hasExistingStripePaidSubscription) {
        isBillingPortalInFlightRef.current = true;
        setIsBillingPortalPending(true);
        try {
          await redirectToBillingPortalUpdate(onCheckoutComplete, {
            tier: effectiveTier,
            period,
          });
        } finally {
          isBillingPortalInFlightRef.current = false;
          setIsBillingPortalPending(false);
        }
        return;
      }
      isCheckoutInFlightRef.current = true;
      performStripeCheckout(period, effectiveTier);
    },
    [
      handleUpgradeByPriceId,
      isCheckoutPending,
      hasExistingStripePaidSubscription,
      redirectToBillingPortalUpdate,
      onCheckoutComplete,
      performStripeCheckout,
    ]
  );

  // `isPending` can lag one frame behind the ref guard; read both so callers can
  // skip duplicate analytics/clicks synchronously when an upgrade is in flight.
  const isUpgradeInFlight = useCallback(
    () =>
      isPending || isBillingPortalInFlightRef.current || isCheckoutInFlightRef.current,
    [isPending]
  );

  return {
    handleUpgrade,
    handleUpgradeByPriceId,
    isPending,
    isUpgradeInFlight,
  };
}
